import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { PresenceAvatar } from "../../components/ui/Presence";
import { subscribeToConversation } from "../../lib/realtime";
import { useChatDock } from "../../stores/chat";
import { SharedTrack } from "./SharedTrack";
import { messages, type Conversation } from "./api";

/**
 * One docked conversation, in two halves: a cell in the bar and a panel above.
 *
 * ## Why they are separate components
 *
 * They are not in the same place. The cell sits in the rail in the order the
 * conversations were docked; the panels tile from the right edge of the bar in
 * the order they were opened, and the two orders do not have to agree.
 *
 * The first version drew the panel inside the tab, anchored to its right edge,
 * which is the arrangement that made two open windows overlap - a tab is about
 * 150px wide and a panel is 320, so the second one opened straight over the
 * first and the one underneath could not be reached without collapsing the one
 * on top. Tiling them is the fix, and tiling means the rail has to place them,
 * so the panel cannot live inside the tab.
 *
 * ## The subscription lives on the tab
 *
 * Deliberately: the tab is rendered for as long as the conversation is docked,
 * and the panel only while it is open. A collapsed tab is exactly the thing
 * that needs to hear about a new message - it is what the unread badge is for -
 * so the socket belongs to the half that is always there.
 */

/** §9.2: 320 by 400, which is enough for a conversation and not a page. */
export const WIDTH = 320;
export const HEIGHT = 400;

function nameOf(conversation: Conversation | null): string {
  return (
    conversation?.other?.display_name
    ?? conversation?.other?.username
    ?? conversation?.title
    ?? "Conversation"
  );
}

/* -------------------------------------------------------------------------- */
/* The cell in the bar                                                        */
/* -------------------------------------------------------------------------- */

export function ChatTab({
  conversationId,
  conversation,
}: {
  conversationId: number;
  conversation: Conversation | null;
}) {
  const client = useQueryClient();
  const open = useChatDock((state) => state.open.includes(conversationId));
  const toggle = useChatDock((state) => state.toggle);

  /*
   * Live for as long as it is docked - open or not.
   *
   * This used to be gated on the window being open, which got the collapsed
   * case exactly backwards: a tab you are not looking at is the one that needs
   * to tell you something arrived, and without a subscription the badge waited
   * on the inbox's 60-second poll.
   *
   * Invalidating the thread while collapsed costs nothing - that query is not
   * mounted, and invalidating an absent query marks it stale rather than
   * fetching it.
   */
  useEffect(
    () =>
      subscribeToConversation(conversationId, () => {
        void client.invalidateQueries({ queryKey: ["conversation", conversationId] });
        void client.invalidateQueries({ queryKey: ["conversations"] });
      }),
    [conversationId, client],
  );

  const who = conversation?.other;
  const name = nameOf(conversation);
  const unread = conversation?.unread ?? 0;

  return (
    <button
      type="button"
      onClick={() => toggle(conversationId)}
      aria-expanded={open}
      data-chat-tab={conversationId}
      className={`flex h-full max-w-[200px] items-center gap-2.5 border-l border-line px-4 transition-colors ${
        open ? "bg-raised text-ink" : "text-soft hover:bg-raised hover:text-ink"
      }`}
    >
      <PresenceAvatar src={who?.avatar} name={name} size={32} online={who?.online} ring="ring-raised" />

      <span className="min-w-0 truncate text-sm font-medium">{name}</span>

      {unread > 0 && !open && (
        <span
          data-chat-unread
          className="shrink-0 rounded-[var(--radius-pill)] bg-accent px-1.5 font-mono text-[10px] text-accent-ink"
        >
          {unread}
        </span>
      )}
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* The panel above it                                                         */
/* -------------------------------------------------------------------------- */

export function ChatPanel({
  conversationId,
  conversation,
}: {
  conversationId: number;
  conversation: Conversation | null;
}) {
  const client = useQueryClient();
  const toggle = useChatDock((state) => state.toggle);
  const close = useChatDock((state) => state.close);

  const [draft, setDraft] = useState("");
  const scroller = useRef<HTMLDivElement | null>(null);

  /* Rendered only while open, so there is no `enabled` to think about. */
  const thread = useQuery({
    queryKey: ["conversation", conversationId],
    queryFn: () => messages.thread(conversationId),
  });

  /* Opening it is what counts as reading it. */
  useEffect(() => {
    void messages
      .markRead(conversationId)
      .then(() => client.invalidateQueries({ queryKey: ["conversations"] }))
      .catch(() => {
        // A failed read receipt leaves a badge on a conversation somebody has
        // seen, which is a smaller problem than an error over their chat.
      });
  }, [conversationId, client, thread.data?.items.length]);

  /*
   * Pinned to the newest message.
   *
   * A chat that opens at the top of the history is one where the thing just
   * said is off screen, which is the opposite of what a window this size is
   * for.
   */
  useEffect(() => {
    if (!scroller.current) return;

    scroller.current.scrollTop = scroller.current.scrollHeight;
  }, [thread.data?.items.length]);

  const send = useMutation({
    mutationFn: (body: string) => messages.send(conversationId, { body }),
    onSuccess: () => {
      setDraft("");
      void client.invalidateQueries({ queryKey: ["conversation", conversationId] });
      void client.invalidateQueries({ queryKey: ["conversations"] });
    },
  });

  const who = conversation?.other;
  const name = nameOf(conversation);

  return (
    <div
      style={{ width: WIDTH, height: HEIGHT }}
      data-chat-window={conversationId}
      className="pointer-events-auto flex shrink-0 flex-col overflow-hidden rounded-t-[var(--radius-panel)] border border-b-0 border-line-lit bg-raised shadow-2xl"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-line px-3 py-2">
        <PresenceAvatar src={who?.avatar} name={name} size={26} online={who?.online} ring="ring-raised" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{name}</span>

        <button
          type="button"
          onClick={() => toggle(conversationId)}
          aria-label="Minimise"
          className="rounded px-1.5 py-1 font-mono text-[11px] text-soft transition-colors hover:text-ink"
        >
          –
        </button>
        <button
          type="button"
          onClick={() => close(conversationId)}
          aria-label="Close"
          data-chat-close
          className="rounded px-1.5 py-1 font-mono text-[11px] text-soft transition-colors hover:text-ink"
        >
          ×
        </button>
      </div>

      <div
        ref={scroller}
        className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-3 py-2.5"
      >
        {thread.isPending && <p className="font-mono text-[10px] text-ghost">loading…</p>}

        {thread.data?.items.length === 0 && (
          <p className="text-xs text-faint">Nothing here yet — say something.</p>
        )}

        {thread.data?.items.map((message) => (
          /*
            A div rather than a paragraph: a shared track is a card, and a card
            inside a <p> is invalid markup the browser silently reparents -
            which put it outside the bubble entirely.
          */
          <div
            key={message.id}
            data-chat-message
            className={`max-w-[85%] rounded-[var(--radius-control)] px-2.5 py-1.5 text-[13px] leading-snug ${
              message.mine ? "self-end bg-accent text-accent-ink" : "self-start bg-slate text-ink"
            }`}
          >
            {message.deleted ? (
              <span className="italic opacity-70">Message removed</span>
            ) : (
              <>
                {message.body}

                {/* Without this a track shared into a conversation somebody has
                    open in the dock is an empty bubble - an attachment-only
                    message has no body at all. */}
                {message.track && (
                  <SharedTrack track={message.track} mine={message.mine} compact />
                )}
              </>
            )}
          </div>
        ))}
      </div>

      <form
        className="flex shrink-0 items-center gap-2 border-t border-line px-2.5 py-2"
        onSubmit={(event) => {
          event.preventDefault();

          const text = draft.trim();

          if (text === "" || send.isPending) return;

          send.mutate(text);
        }}
      >
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Message…"
          aria-label={`Message ${name}`}
          data-chat-input
          className="min-w-0 flex-1 rounded-[var(--radius-pill)] border border-line bg-app px-3 py-1.5 text-[13px] text-ink outline-none transition-colors placeholder:text-faint focus:border-line-strong"
        />
        <button
          type="submit"
          disabled={draft.trim() === "" || send.isPending}
          className="meta-caps shrink-0 rounded-[var(--radius-pill)] bg-accent px-3 py-1.5 text-accent-ink transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          Send
        </button>
      </form>
    </div>
  );
}
